import AdminPagination from '../AdminPagination';
import AdminState from '../AdminState';
import { ChevronDownIcon, RepeatIcon } from '../AdminIcon';
// Components/Tabs/AuditLogTab.jsx

import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Slider from "../Slider/Slider";
import Backend from "../../../utils/api.js";
import { Box, Flex, Grid, Text, VStack, HStack, Badge, Select, FormControl, FormLabel, Table, Thead, Tbody, Tr, Th, Td, IconButton } from '@chakra-ui/react';

import "./AuditLogTab.css";

// Action type display names and colors
const ACTION_CONFIG = {
  USER_SEARCH: { label: "User search", color: "blue" },
  USER_VIEW: { label: "User view", color: "cyan" },
  BADGE_KEY_REVEAL: { label: "Badge key reveal", color: "orange" },
  BADGE_RESET: { label: "Badge connection purge", color: "red" },
  FORCE_LOGOUT: { label: "Check out", color: "blue" },
  SESSION_VIEW: { label: "Session view", color: "purple" },
  SWIPE_HISTORY_VIEW: { label: "Check-in history view", color: "teal" },
  LAB_SWIPE_LOG_VIEW: { label: "Check in log view", color: "indigo" },
  LAB_STATISTICS_VIEW: { label: "Space statistics view", color: "yellow" },
  MACHINE_ACCESS_VIEW: { label: "Tool instance access view", color: "green" },
};

/**
 * AuditLogTab Component
 * 
 * Displays a paginated audit log of admin actions with filtering capabilities.
 * Super admins can select which lab to view logs for, or view all logs across Pinpoint.
 */
function AuditLogTab({ user, labId, revert }) {
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [actionFilter, setActionFilter] = useState("");
  const [selectedLab, setSelectedLab] = useState(labId || "all");
  const [labs, setLabs] = useState([]);
  const [labsError, setLabsError] = useState(false);
  const [labsLoading, setLabsLoading] = useState(false);

  const loadLabs = async () => {
    setLabsLoading(true);
    setLabsError(false);
    try {
      const response = await Backend.getLabs(1000);
      if (response.status !== 200) throw new Error('spaces_failed');
      setLabs(response.items || []);
    } catch {
      setLabsError(true);
    } finally {
      setLabsLoading(false);
    }
  };

  useEffect(() => {
    if (user.isSuperAdmin) loadLabs();
  }, [user.isSuperAdmin]);

  useEffect(() => {
    loadAuditLogs();
  }, [page, actionFilter, selectedLab]);

  const loadAuditLogs = async () => {
    setLoading(true);
    setError(null);

    try {
      const filters = {};
      if (selectedLab && selectedLab !== "all") {
        filters.target_lab = selectedLab;
      }
      if (actionFilter) {
        filters.action = actionFilter;
      }

      const response = await Backend.adminGetAuditLog(user.UID, filters, page, 25);

      if (response.status === 200) {
        setHasLoaded(true);
        setAuditLogs(response.data || []);
        setTotalPages(Math.ceil((response.total || 0) / (response.per_page || 25)) || 1);
        setTotal(response.total || 0);
      } else if (response.status === 403) {
        setError("You do not have permission to view the audit log");
      } else {
        setError("Failed to load audit log");
      }
    } catch (err) {
      console.error("Error loading audit log:", err);
      setError("Failed to connect to the server");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  const getActionBadge = (action) => {
    const config = ACTION_CONFIG[action] || { label: action, color: "gray" };
    return (
      <Badge colorScheme={config.color} fontSize="xs">
        {config.label}
      </Badge>
    );
  };

  const parseDetails = (detailsStr) => {
    if (!detailsStr) return null;
    try {
      return JSON.parse(detailsStr);
    } catch {
      return null;
    }
  };

  return (
    <Slider
      name="Audit log"
      navColor="rgb(220, 53, 69)"
      slide={true}
      index={0}
      onClick={() => revert(1)}
    >
      <div className={`AuditLogTab${loading && hasLoaded ? ' admin-refreshing' : ''}`} aria-busy={loading}>
        <Box className="filters-section" mb={4}>
          <Flex align="end" justify="space-between" gap={4} flexWrap="wrap">
            <Grid gap={4} minW={0} flex={{ base: '1 1 100%', md: '0 1 auto' }}
              templateColumns={user.isSuperAdmin
                ? { base: 'repeat(2, minmax(0, 1fr))', md: 'repeat(2, 200px)' }
                : 'minmax(0, 200px)'}>
              {user.isSuperAdmin && (
                <FormControl minW={0}>
                  <FormLabel>Space</FormLabel>
                  <Select icon={<ChevronDownIcon />} aria-label="Space"
                    isDisabled={labsLoading || labsError} value={selectedLab}
                    onChange={(event) => { setSelectedLab(event.target.value); setPage(1); }}>
                    <option value="all">All spaces</option>
                    {labs.map((lab) => (
                      <option key={lab.id} value={lab.id}>{lab.labName || lab.id}</option>
                    ))}
                  </Select>
                </FormControl>
              )}
              <FormControl minW={0}>
                <FormLabel>Action</FormLabel>
                <Select icon={<ChevronDownIcon />} aria-label="Action" placeholder="All actions"
                  value={actionFilter}
                  onChange={(event) => { setActionFilter(event.target.value); setPage(1); }}>
                  {Object.entries(ACTION_CONFIG).map(([key, config]) => (
                    <option key={key} value={key}>{config.label}</option>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <HStack spacing={4} align="center" justify="space-between" flexShrink={0}
              width={{ base: '100%', md: 'auto' }}>
              <Text fontSize="sm" color="gray.600" whiteSpace="nowrap">{total} total entries</Text>
              <IconButton icon={<RepeatIcon />} onClick={loadAuditLogs}
                aria-label="Refresh audit log" isLoading={loading} variant="outline" />
            </HStack>
          </Flex>
        </Box>

        {labsError && <AdminState status="error" title="Space filter could not be loaded"
          description="You can still view audit activity. Try again to filter it by space." onRetry={loadLabs} />}

        {/* Error Display */}
        {error && (
          <AdminState status="error" title="Audit log could not be loaded" description={error} onRetry={loadAuditLogs} />
        )}

        {/* Loading State */}
        {loading && !hasLoaded ? (
          <AdminState status="loading" title="Loading audit log…" />
        ) : error ? null : (
          <>
            {/* Audit log Table */}
            <Box className="audit-table-container" tabIndex={0} role="region" aria-label="Audit log table; scroll horizontally to see more">
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Timestamp</Th>
                    <Th>Admin</Th>
                    <Th>Action</Th>
                    {selectedLab === "all" && <Th>Space</Th>}
                    <Th>Target</Th>
                    <Th>Details</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {auditLogs.length === 0 ? (
                    <Tr>
                      <Td colSpan={selectedLab === "all" ? 6 : 5} textAlign="center" py={8}>
                        <AdminState title="No audit log entries found" description="Admin activity matching the selected space and action will appear here." mb={0} textAlign="left" />
                      </Td>
                    </Tr>
                  ) : (
                    auditLogs.map((log) => {
                      const details = parseDetails(log.details);
                      return (
                        <Tr key={log.id}>
                          <Td whiteSpace="nowrap" fontSize="sm">
                            {formatDate(log.timestamp)}
                          </Td>
                          <Td>
                            <VStack align="start" spacing={0}>
                              <Text fontSize="sm" fontWeight="medium">
                                {log.admin_name || `UID: ${log.admin_uid}`}
                              </Text>
                              {log.ip_address && (
                                <Text fontSize="xs" color="gray.600">
                                  {log.ip_address}
                                </Text>
                              )}
                            </VStack>
                          </Td>
                          <Td>{getActionBadge(log.action)}</Td>
                          {selectedLab === "all" && (
                            <Td>
                              {log.target_lab ? (
                                <Badge colorScheme="gray" fontSize="xs">
                                  {log.target_lab}
                                </Badge>
                              ) : (
                                <Text fontSize="sm" color="gray.600">—</Text>
                              )}
                            </Td>
                          )}
                          <Td>
                            {log.target_name ? (
                              <Text fontSize="sm">{log.target_name}</Text>
                            ) : log.target_uid ? (
                              <Text fontSize="sm" color="gray.600">
                                UID: {log.target_uid}
                              </Text>
                            ) : log.target_lab ? (
                              <Text fontSize="sm" color="gray.600">
                                Space: {log.target_lab}
                              </Text>
                            ) : (
                              <Text fontSize="sm" color="gray.600">—</Text>
                            )}
                          </Td>
                          <Td>
                            {details ? (
                              <Text fontSize="xs" color="gray.600" maxW="200px" isTruncated>
                                {details.query && `Query: "${details.query}"`}
                                {details.type === "bulk_logout" && 
                                  `${details.users_affected} users, ${details.sessions_closed} sessions`}
                              </Text>
                            ) : (
                              <Text fontSize="sm" color="gray.600">—</Text>
                            )}
                          </Td>
                        </Tr>
                      );
                    })
                  )}
                </Tbody>
              </Table>
            </Box>

            {/* Pagination */}
            <AdminPagination page={page} totalPages={totalPages} totalItems={total} pageSize={25} isLoading={loading} onPageChange={setPage} itemLabel="entries" />
          </>
        )}
      </div>
    </Slider>
  );
}

AuditLogTab.propTypes = {
  user: PropTypes.object.isRequired,
  labId: PropTypes.string,
  revert: PropTypes.func.isRequired,
};

export default AuditLogTab;
